import QRCode from "qrcode";
import { LOGO_CIRCLE, LOGO_INNER, QR_BLACK, logoBox, logoFit } from "../../qrStyle.js";

const loadImage = (src, crossOrigin) => new Promise((resolve) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    // Si el logo no se puede leer (por ejemplo, sin permiso de CORS), el QR y el cartel salen sin logo.
    img.onerror = () => resolve(null);
    img.src = src;
});

// Mira las esquinas del logo en chiquito para saber si se puede recortar en círculo (qrStyle.js).
const fitOf = (img) => {
    try {
        const size = 48;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        // Igual que al recortarlo: el cuadrado del medio del logo.
        const side = Math.min(img.naturalWidth, img.naturalHeight);
        ctx.drawImage(img, (img.naturalWidth - side) / 2, (img.naturalHeight - side) / 2, side, side, 0, 0, size, size);
        return logoFit(ctx.getImageData(0, 0, size, size).data, size, img.naturalWidth, img.naturalHeight);
    } catch (error) {
        return "contain";
    }
}

// Dibuja el logo en un círculo blanco en el centro del QR.
const drawLogo = (ctx, logo, width) => {
    const c = width / 2;
    const outer = width * LOGO_CIRCLE;
    ctx.save();
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(c, c, outer / 2, 0, Math.PI * 2);
    ctx.fill();

    const d = outer * LOGO_INNER;
    const fit = fitOf(logo);
    const box = logoBox(fit, logo.naturalWidth, logo.naturalHeight, d);
    ctx.beginPath();
    ctx.arc(c, c, d / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(logo, c - box.w / 2, c - box.h / 2, box.w, box.h);
    ctx.restore();
}

// QR como imagen PNG. Con `logo` (link de la imagen) va en el centro y el QR usa corrección
// de errores H para seguir leyéndose; `color` es el de los módulos.
export const qrDataUrl = async (text, width = 1024, { color = QR_BLACK, logo = "" } = {}) => {
    const plain = await QRCode.toDataURL(text, { width, margin: 2, errorCorrectionLevel: logo ? "H" : "M", color: { dark: color, light: "#ffffff" } });
    if (!logo) return plain;
    const [qr, img] = await Promise.all([loadImage(plain), loadImage(logo, true)]);
    if (!img) return plain;
    const canvas = document.createElement("canvas");
    canvas.width = qr.width;
    canvas.height = qr.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(qr, 0, 0);
    drawLogo(ctx, img, canvas.width);
    try {
        return canvas.toDataURL("image/png");
    } catch (error) {
        // El navegador no dejó leer el logo: el QR sale sin logo pero con la corrección H.
        return plain;
    }
}

// `style`: { color, logo } como en qrDataUrl.
export const downloadQr = async (text, filename, style) => {
    const link = document.createElement("a");
    link.href = await qrDataUrl(text, 1024, style);
    link.download = filename;
    link.click();
}

// Tamaños del cartel. Los dos salen en hoja A4: el grande ocupa la hoja entera y el
// chico (A6, para mesas) va 4 veces por hoja con líneas para recortar.
export const SIGN_SIZES = {
    grande: { label: "Grande", detail: "Una hoja A4, para la vidriera o la pared", perPage: 1 },
    chico: { label: "Chico", detail: "4 por hoja A4 (tamaño A6), para mesas y mostrador", perPage: 4 }
};

// A4 a 250 dpi.
const PAGE_W = 2067;
const PAGE_H = 2923;

const FONT = "'Plus Jakarta Sans', system-ui, sans-serif";

// Parte el nombre en hasta dos líneas y achica la letra si hace falta.
const fitName = (ctx, text, maxWidth, size) => {
    for (let s = size; s > size * 0.5; s -= size * 0.05) {
        ctx.font = `800 ${s}px ${FONT}`;
        if (ctx.measureText(text).width <= maxWidth) return { size: s, lines: [text] };
        const words = text.split(" ");
        for (let i = words.length - 1; i > 0; i--) {
            const a = words.slice(0, i).join(" ");
            const b = words.slice(i).join(" ");
            if (ctx.measureText(a).width <= maxWidth && ctx.measureText(b).width <= maxWidth) return { size: s, lines: [a, b] };
        }
    }
    ctx.font = `800 ${size * 0.5}px ${FONT}`;
    return { size: size * 0.5, lines: [text] };
}

const roundedImage = (ctx, img, x, y, size, radius) => {
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.arcTo(x + size, y, x + size, y + size, radius);
    ctx.arcTo(x + size, y + size, x, y + size, radius);
    ctx.arcTo(x, y + size, x, y, radius);
    ctx.arcTo(x, y, x + size, y, radius);
    ctx.closePath();
    ctx.clip();
    // El logo se ajusta sin deformarse, centrado en el cuadrado.
    const scale = Math.min(size / img.width, size / img.height);
    const w = img.width * scale;
    const h = img.height * scale;
    ctx.drawImage(img, x + (size - w) / 2, y + (size - h) / 2, w, h);
    ctx.restore();
}

// Dibuja un cartel en el rectángulo (x, y, w, h): logo, nombre, QR, llamado y link.
const drawSign = (ctx, { x, y, w, h }, { name, url, qr, logo }) => {
    const pad = w * 0.09;
    const inner = w - pad * 2;
    const logoSize = logo ? w * 0.15 : 0;
    const title = fitName(ctx, name, inner, w * 0.075);
    const titleH = title.lines.length * title.size * 1.12;
    const qrSize = w * 0.6;
    const ctaSize = w * 0.05;
    const urlSize = w * 0.031;
    const gap = w * 0.045;

    const total = (logo ? logoSize + gap : 0) + titleH + gap + qrSize + gap + ctaSize * 1.2 + gap * 0.4 + urlSize * 1.2;
    let top = y + (h - total) / 2;
    const cx = x + w / 2;

    ctx.textAlign = "center";
    ctx.textBaseline = "top";

    if (logo) {
        roundedImage(ctx, logo, cx - logoSize / 2, top, logoSize, logoSize * 0.2);
        top += logoSize + gap;
    }

    ctx.fillStyle = "#1e1520";
    ctx.font = `800 ${title.size}px ${FONT}`;
    title.lines.forEach((line, i) => ctx.fillText(line, cx, top + i * title.size * 1.12));
    top += titleH + gap;

    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(qr, cx - qrSize / 2, top, qrSize, qrSize);
    ctx.imageSmoothingEnabled = true;
    top += qrSize + gap;

    ctx.font = `800 ${ctaSize}px ${FONT}`;
    ctx.fillText("Escaneá para ver el menú", cx, top);
    top += ctaSize * 1.2 + gap * 0.4;

    ctx.fillStyle = "#5e5462";
    ctx.font = `500 ${urlSize}px ${FONT}`;
    ctx.fillText(url.replace(/^https?:\/\//, "").replace(/\?.*$/, ""), cx, top);
}

// Hoja A4 con el cartel (o los 4 carteles chicos) dibujada en un canvas. `url` es la del QR
// (con ?o=qr); en el texto del cartel va sin el parámetro. `style` es el del QR (qrDataUrl).
export const renderSignPage = async (business, url, size = "grande", style) => {
    await Promise.all([
        document.fonts?.load(`800 40px 'Plus Jakarta Sans'`),
        document.fonts?.load(`500 40px 'Plus Jakarta Sans'`)
    ]).catch(() => {});

    const [qr, logo] = await Promise.all([
        // Del tamaño en que va en la hoja (el 60% del ancho del cartel), así no se reescala.
        qrDataUrl(url, Math.round((size === "chico" ? PAGE_W / 2 : PAGE_W * 0.88) * 0.6), style).then((src) => loadImage(src)),
        business.logo ? loadImage(business.logo, true) : null
    ]);

    const canvas = document.createElement("canvas");
    canvas.width = PAGE_W;
    canvas.height = PAGE_H;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, PAGE_W, PAGE_H);

    const content = { name: business.nombreEmpresa || business.usuarioEmpresa, url, qr, logo };

    if (size === "chico") {
        const w = PAGE_W / 2;
        const h = PAGE_H / 2;
        [[0, 0], [w, 0], [0, h], [w, h]].forEach(([x, y]) => drawSign(ctx, { x, y, w, h }, content));
        // Líneas de corte.
        ctx.strokeStyle = "#c9c1cc";
        ctx.lineWidth = 3;
        ctx.setLineDash([24, 18]);
        ctx.beginPath();
        ctx.moveTo(w, 0);
        ctx.lineTo(w, PAGE_H);
        ctx.moveTo(0, h);
        ctx.lineTo(PAGE_W, h);
        ctx.stroke();
    } else {
        drawSign(ctx, { x: PAGE_W * 0.06, y: 0, w: PAGE_W * 0.88, h: PAGE_H }, content);
    }

    return canvas;
}

// PDF de una página A4 con la imagen JPEG del cartel. Se arma a mano para no sumar
// una librería: alcanza con un objeto de imagen (DCTDecode) que ocupa toda la hoja.
const jpegPdf = (jpeg, width, height) => {
    const W = 595.28;
    const H = 841.89;
    const enc = new TextEncoder();
    const parts = [];
    const offsets = [];
    let length = 0;
    const add = (chunk) => {
        const bytes = typeof chunk === "string" ? enc.encode(chunk) : chunk;
        parts.push(bytes);
        length += bytes.length;
    };
    const obj = (n, body) => {
        offsets[n] = length;
        add(`${n} 0 obj\n`);
        body.forEach(add);
        add("\nendobj\n");
    };

    const draw = `q ${W} 0 0 ${H} 0 0 cm /Im0 Do Q`;
    add("%PDF-1.4\n%\xE2\xE3\xCF\xD3\n");
    obj(1, ["<< /Type /Catalog /Pages 2 0 R >>"]);
    obj(2, ["<< /Type /Pages /Kids [3 0 R] /Count 1 >>"]);
    obj(3, [`<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${W} ${H}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>`]);
    obj(4, [`<< /Type /XObject /Subtype /Image /Width ${width} /Height ${height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\nstream\n`, jpeg, "\nendstream"]);
    obj(5, [`<< /Length ${draw.length} >>\nstream\n${draw}\nendstream`]);

    const xref = length;
    add(`xref\n0 6\n0000000000 65535 f \n`);
    for (let n = 1; n <= 5; n++) add(String(offsets[n]).padStart(10, "0") + " 00000 n \n");
    add(`trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`);

    return new Blob(parts, { type: "application/pdf" });
}

const dataUrlBytes = (dataUrl) => {
    const binary = atob(dataUrl.split(",")[1]);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return bytes;
}

export const downloadSignPdf = async (business, url, size, filename, style) => {
    const canvas = await renderSignPage(business, url, size, style);
    const blob = jpegPdf(dataUrlBytes(canvas.toDataURL("image/jpeg", 0.92)), canvas.width, canvas.height);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 10000);
}

// Abre la hoja del cartel en una ventana nueva, lista para imprimir.
export const printSign = async (business, url, size = "grande", style) => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return false;

    const page = (await renderSignPage(business, url, size, style)).toDataURL("image/png");
    printWindow.document.write(`<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8">
<title>Cartel QR</title>
<style>
  @page { size: A4; margin: 0; }
  body { margin: 0; background: #fff; }
  img { display: block; width: 210mm; height: 297mm; }
</style>
</head>
<body>
<img src="${page}" alt="Cartel con el código QR del menú">
<script>
  window.onload = function () { setTimeout(function () { window.focus(); window.print(); }, 300); };
</script>
</body>
</html>`);
    printWindow.document.close();
    return true;
}
